import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FDBodyText, FDStack } from '@facet-wealth/design';
import fr from 'facet-rest';
import AggregatorModalPicker
  from 'Components/UI/Accounts/AccountManagement/AccountSyncStatusPicker/AggregatorModalPicker/AggregatorModalPicker';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { UnlinkInstitutionButton } from './UnlinkInstitutionButton';
import { InstitutionActionButton } from './InstitutionActionButton';
import { AccountInstitutionStatusIndicator } from './AccountInstitutionStatusIndicator';
import { AccountRefreshType } from './Consts';


export const AccountInstitution = (props) => {
  const {
    collection, refreshCollection, onDeleteSuccess, onActionSuccess, oldAesthetic, horizontalAlignment, hideUnlink,
  } = props;
  const [showModal, setShowModal] = useState(false); // Should we show the institution refresh/re-link modal?
  const [isRefreshing, setIsRefreshing] = useState(false); // Is the refresh ongoing?

  const { displayErrorMessage, displaySuccess, displayInfo } = useSnackbarContext();
  const resource = collection.resources()?.head();

  // Exit early if it's not a linked institution. We don't expect that this will ever be the case, but just including this as a safeguard
  if (!resource || !resource.attr('accountAggregatorMetaId')) {
    return null;
  }

  const lastSync = resource.attr('formattedLastSync');
  const actionLinkRelationship = resource.withRelationship(fr.relationships.actionLink.name, (rel) => rel, () => null);

  // Sync handlers
  const onSyncStarted = () => {
    setIsRefreshing(true);
  };

  const onSyncComplete = async () => {
    refreshCollection();
    onActionSuccess();
    setIsRefreshing(false);
  };

  const onCloseAggregatorModal = () => {
    setShowModal(false);
  };

  // Refresh handlers
  const onRefreshSuccess = () => {
    displaySuccess(`${resource.attr('institution')} successfully refreshed`);
    refreshCollection(); // Refresh the linkedInstitution collection
    onActionSuccess(); // Do whatever onCompletion handler we got from props
  };

  const onRefreshComplete = () => {
    setShowModal(false);
    setIsRefreshing(false);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);

    // get the current lastSync
    const prevLastSync = resource.attr('lastSync');

    try {
      // refresh the accounts
      await resource.relationship(fr.relationships.syncAccounts.name).link('related').Post({}, {
        raw: true,
        deprecatedAllowAlterOfRelation: true,
      });

      // get the latest resource and check if lastSync has changed
      const [latestResource] = await resource.link('self').get();
      if (latestResource.attr('lastSync') === prevLastSync) {
        displayInfo('The current information is the most up-to date information we can provide at this time.');
        return;
      }

      onRefreshSuccess(); // TODO WHY DIDNT THIS REFRESH THE PAGE COLLECTION?
    } catch (err) {
      displayErrorMessage(err, `Failed to refresh ${resource.attr('institution')}`);
    } finally {
      onRefreshComplete();
    }
  };

  // Refresh/re-link button handler
  const handleClickActionLink = async () => {
    const type = actionLinkRelationship.meta('type');

    if (type === AccountRefreshType.fix || type === AccountRefreshType.manualRefresh) {
      setShowModal(true);
    }

    if (type === AccountRefreshType.refresh) {
      await handleRefresh();
    }
  };

  return (
    <FDStack direction={'column'} spacing={1}>
      <FDStack direction={'row'} wrap spacing={1} justifyContent={horizontalAlignment} alignItems={'center'}>
        {oldAesthetic && (
          <>
            <AccountInstitutionStatusIndicator resource={resource} alwaysCircleIcon size="sm" />
            <FDBodyText value={`Last sync: ${lastSync}`} size={'sm'} />
          </>
        )}
        {actionLinkRelationship ? <InstitutionActionButton
          actionLinkRelationship={actionLinkRelationship}
          institutionResource={resource}
          isDisabled={showModal || isRefreshing}
          handleOnClick={handleClickActionLink}
          onSyncStarted={onSyncStarted}
          onSyncComplete={onSyncComplete}
          onClose={onCloseAggregatorModal}
          source={resource.attr('source')}
        /> : null}
        {!oldAesthetic && (
          <>
            <AccountInstitutionStatusIndicator resource={resource} alwaysCircleIcon={false} size="md" />
            <FDBodyText value={lastSync} size={'sm'} />
          </>
        )}
        {!hideUnlink && <UnlinkInstitutionButton resource={resource} onDeleteSuccess={onDeleteSuccess} />}
      </FDStack>
      {showModal && (
        <AggregatorModalPicker
          source={actionLinkRelationship.meta('source')}
          resource={resource}
          aggregatorLink={actionLinkRelationship.link('related')}
          onSyncStarted={onSyncStarted}
          onComplete={onSyncComplete}
          onClose={onCloseAggregatorModal}
        />)
       }
    </FDStack>
  );
};

AccountInstitution.defaultProps = {
  oldAesthetic: false,
  horizontalAlignment: 'flex-start',
  hideUnlink: false,
};

AccountInstitution.propTypes = {
  hideUnlink: PropTypes.bool,
  refreshCollection: PropTypes.func.isRequired, // refresh the linkedInstitution collection
  collection: PropTypes.object.isRequired, // the linkedInstitution collection
  onDeleteSuccess: PropTypes.func.isRequired, // on close of the account modal
  onActionSuccess: PropTypes.func.isRequired, // on completion of whatever the action link did
  oldAesthetic: PropTypes.bool,
  horizontalAlignment: PropTypes.oneOf(['flex-start', 'flex-end']),
};
